import { Masonry } from "@mui/lab"
import { Box, Grid, Typography, alpha, styled, useMediaQuery, useTheme } from "@mui/material"
import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { FavouriteWithState, SetFavourite, SetItemStates, TileInstitutionData, Trial } from "@/api/dtos"
import ServiceStackClientContext from "@/contexts/api/ServiceStackClientContext"
import gridClasses from "@mui/material/Grid/gridClasses"
import Measure from 'react-measure'
import TrialTile from "./Tiles/Main/TrialTile"

const MasonryBox = styled(Box)(({ theme }) => ({
    margin: theme.spacing(2, 0, 2, 2),
    width: 'auto',
}))

const FavouriteHeading = styled(Typography)(({ theme }) => ({
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    
    color: theme.palette.mode === 'dark' ?
    undefined :
    theme.palette.secondary.main,
}))

const TileHeading = styled(Typography)(({ theme }) => ({
    borderTop: '1px solid',
    borderColor: alpha(theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black, 0.12),
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    color: theme.palette.mode === 'dark' ?
    undefined :
    theme.palette.secondary.main,
}))

//calc(20.00% - 16px)
interface FavouriteGridProps {
    numberOfColumns: number;
}

const FavouriteGrid = styled(Grid, { shouldForwardProp: propName => propName !== 'numberOfColumns' })<FavouriteGridProps>(({ numberOfColumns, theme }) => {
    const spacing = theme.spacing(2);
    const spacingInt = parseInt(spacing.replace('px', ''), 10);
    const width = `calc(${(100/numberOfColumns).toFixed(5)}% - ${(((numberOfColumns - 1) * spacingInt) / numberOfColumns)}px)`;
    //const width = `calc(${(100/numberOfColumns).toFixed(5)}% - ${spacing})`;

    return {
        display: 'flex',
        flexWrap: 'nowrap',
        //alignItems: 'flex-start',
        alignItems: 'st',
        gap: spacing,
        paddingLeft: spacing,
        paddingRight: spacing,
        paddingTop: spacing,
        paddingBottom: spacing,
        overflow: 'auto',

        [`& > .${gridClasses.item}`]: {
            minWidth: width,
            maxWidth: width
        },
        
        '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px'
        },
        '&::-webkit-scrollbar-track': {
            WebkitBoxShadow: `inset 0 0 6px ${theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.2) : alpha(theme.palette.common.black, 0.2)}`
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.1) : alpha(theme.palette.common.black, 0.1),
            outline: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.2) : alpha(theme.palette.common.black, 0.2),
            borderRadius: theme.shape.borderRadius
        }
    }   
})


export interface IInstitution {
    id: number;
    name: string;
}

export interface ITrial
{
    id: number;
    name: string;
    protocolId: string;
    logo?: {
        light?: string;
        dark?: string;
        desktopHeight?: string;
        desktopWidth?: string;
        mobileHeight?: string;
        mobileWidth?: string;
    };
    favourite: boolean;
    expandedMenuItemStates: Record<number, boolean> | undefined;
    trialTileData: TileInstitutionData | null;
    institutions?: IInstitution[] | null;
}


const createFavourite = (trial: Trial, initialFavourites: FavouriteWithState[] | undefined) => {
    if (initialFavourites) {

        return initialFavourites.some(x => x.trialId == trial.id?.toString());

    } else {
        return false;
    }
}

const createExpandedMenuItemStates = (trial: Trial, initialFavourites: FavouriteWithState[] | undefined) => {
    if (initialFavourites) {
        const favourite = initialFavourites.find(x => x.trialId == trial.id?.toString());
        if (favourite) {
            return favourite.favouriteExpandItemStates?.reduce((record, x) => {
                if (x.expandStateMenuItemId) {
                    record[x.expandStateMenuItemId] = x.open ?? false;
                }
                return record;
            }, {} as Record<number, boolean>) ?? {};
        }
    }
}

const createTrials = (trials: Trial[] | undefined, initialFavourites: FavouriteWithState[] | undefined) => {

    if (trials) {
        const output = trials.map((trial) => {
            return {
                id: trial.id ?? 0,
                name: trial.name ?? 'Unknown',
                protocolId: trial.protocolId ?? 'Unknown',
                logo: trial.logo,
                favourite: createFavourite(trial, initialFavourites),
                expandedMenuItemStates: createExpandedMenuItemStates(trial, initialFavourites),
                trialTileData: null,
            }
        });
    
        return output;
    }

    return undefined;
}


interface ITrialsAreaProps {
    trials: Trial[];
    initialFavourites: FavouriteWithState[] | undefined;
}

const TrialsArea: React.FunctionComponent<ITrialsAreaProps> = ({
    trials,
    initialFavourites
}) => {
    const theme = useTheme();

    const isExtraSmall = useMediaQuery(theme.breakpoints.only('xs'));
    const isSmall = useMediaQuery(theme.breakpoints.only('sm'));
    const isMedium = useMediaQuery(theme.breakpoints.only('md'));
    const isLarge = useMediaQuery(theme.breakpoints.only('lg'));
    
    
    const [trialTiles, setTrialTiles] = useState(() => createTrials(trials, initialFavourites));

    useEffect(() => {
        setTrialTiles(createTrials(trials, initialFavourites));
    }, [trials, initialFavourites])

    const client = useContext(ServiceStackClientContext);

    const favourites = useMemo(() => {
        if (trialTiles) {
            return trialTiles.filter((x) => x.favourite)
        }
    }, [trialTiles]);

    const trialsCollapable = useMemo(() => {
        if (trialTiles) {
            return trialTiles.filter((x) => !x.favourite)
        }
    }, [trialTiles]);


    const toggleFavourite = useCallback((id: number) => {

        setTrialTiles(c => {
            if (c) {

                const newData = [...c];
    
                const index = newData.findIndex(x => x.id === id);
    
                newData[index] = { ... newData[index], favourite: !newData[index].favourite };

                return newData;
            }
        })

        toggleAllExpandedMenuItem(id);

        client.post(new SetFavourite({trialId: id}));
        
        // refreshFavourites();

    }, [setTrialTiles])

    const toggleExpandedMenuItem = useCallback((trialId: number, expandMenuItemId: number) => {

        setTrialTiles(c => {
            if (c) {

                const newData = [...c];
    
                const index = newData.findIndex(x => x.id === trialId);
    
                const newExpandedMenuItemStates = { ...newData[index].expandedMenuItemStates };
    
                newExpandedMenuItemStates[expandMenuItemId] = !newExpandedMenuItemStates[expandMenuItemId];
    
                newData[index] = { ...newData[index], expandedMenuItemStates: newExpandedMenuItemStates };
    
                return newData;
            }
        })

        client.post(new SetItemStates({trialId: trialId, expandMenuItemId: expandMenuItemId}));

    }, [setTrialTiles])

    const toggleAllExpandedMenuItem = useCallback((trialId: number) => {
        setTrialTiles(c => {
            if (c) {

                const newData = [...c];
    
                const index = newData.findIndex(x => x.id === trialId);
    
                const newExpandedMenuItemStates: Record<number, boolean> = { ...newData[index].expandedMenuItemStates };
    
                for (const key in newExpandedMenuItemStates) {
                    if (newExpandedMenuItemStates.hasOwnProperty(key)) {
                        newExpandedMenuItemStates[key] = false;
                    }
                }
    
                newData[index] = { ...newData[index], expandedMenuItemStates: newExpandedMenuItemStates };
    
                return newData;
            }
        })

    }, [setTrialTiles])

    const columns = useMemo(() => {
        return isExtraSmall ?
            1 :
            isSmall ?
                2 :
                isMedium ?
                    3 :
                    isLarge ?
                        4 :
                        5;
    }, [isExtraSmall, isSmall, isMedium, isLarge]);

    const [_, setState] = useState(0);

    const updateLayout = useCallback(() => {
        setState(c => c + 1)
    }, [setState]);


    return (
        <>
            {/* Favourites */}
            
            {
                favourites && favourites.length > 0 && (
                    <>
                        <FavouriteHeading
                            variant="h1"
                        >
                            Favourites
                        </FavouriteHeading>
                        <Box>
                            <FavouriteGrid
                                container
                                numberOfColumns={columns}
                            >   
                                {
                                    favourites.map((x) => (
                                        <TrialTile
                                            key={x.id}
                                            trial={x} 
                                            favourite
                                            toggleFavourite={toggleFavourite}
                                            toggleExpandedMenuItem={toggleExpandedMenuItem}
                                        />

                                    ))
                                }
                            </FavouriteGrid>
                        </Box>
                    </>

                )
            }
            

            


            {/* Trials */}
            <TileHeading
                variant="h1"
            >
                Trials
            </TileHeading>

            <MasonryBox>
                <Masonry
                    spacing={2}
                    columns={columns}
                >
                    {
                        trialsCollapable !== undefined && 
                            trialsCollapable.map((x) => (

                                <Measure key={x.id} >
                                    {({ measureRef }) => (
                                        <div ref={measureRef} style={{ minHeight: "96px"}}>
                                            <div style={{flexShrink: 0}}>
                                                <TrialTile 
                                                    key={x.id}
                                                    trial={x}
                                                    favourite={false}
                                                    toggleFavourite={toggleFavourite}
                                                    onExpand={updateLayout}
                                                />

                                            </div>
                                        
                                        </div>
                                    )}
                                </Measure>

                            ))
                        }

                </Masonry>
            </MasonryBox>

        </>
    )
}

export default TrialsArea
